import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials sends the httpOnly cc_token cookie set by the backend on login.
// No JWT ever touches JavaScript-accessible storage — XSS-safe.
export const api = axios.create({ baseURL: API, withCredentials: true });

// ---------------------------------------------------------------------------
// Paused-preview interceptor
// ---------------------------------------------------------------------------
// When the Emergent preview is in "Static/Paused" mode, the ingress serves an
// HTML "Wake up servers" page in response to every URL — including
// /api/auth/login. That HTML reaches axios instead of the backend's JSON, which
// used to surface as a misleading "Invalid password / Login failed" toast even
// though the credentials were correct.
//
// This interceptor:
//   1. Detects the paused-preview HTML response (Content-Type text/html or a
//      body that begins with <!DOCTYPE / <html>).
//   2. Waits a short beat (which is usually enough for the ingress to finish
//      waking the backend) and transparently retries the same request once.
//   3. If the second attempt is still HTML, it converts the response into a
//      proper axios-shaped rejection with a clear detail message so the UI
//      can show an accurate hint instead of a wrong-password error.
//
// It DOES NOT change any credentials, endpoints, or business logic.
const PAUSED_DETAIL =
  "Preview is paused — please wait a moment or click 'Resume Preview', then retry.";

function _looksLikeWakePage(response) {
  if (!response) return false;
  const ct = (response.headers?.["content-type"] || response.headers?.["Content-Type"] || "").toLowerCase();
  const body = response.data;
  const bodyStart =
    typeof body === "string"
      ? body.slice(0, 200).toLowerCase()
      : "";
  const looksHtml =
    ct.includes("text/html") ||
    bodyStart.startsWith("<!doctype") ||
    bodyStart.startsWith("<html");
  const hasWakeMarker =
    bodyStart.includes("wake up") ||
    bodyStart.includes("start your preview") ||
    bodyStart.includes("preview will be ready");
  return looksHtml || hasWakeMarker;
}

function _wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

api.interceptors.response.use(
  async (response) => {
    // If a 2xx came back but the body is the paused-preview HTML page, treat
    // as a transient outage: retry once after 1.2s.
    if (_looksLikeWakePage(response) && !response.config.__pauseRetried) {
      response.config.__pauseRetried = true;
      await _wait(1200);
      try {
        return await api.request(response.config);
      } catch (e) {
        return Promise.reject(e);
      }
    }
    if (_looksLikeWakePage(response)) {
      // Second attempt still paused — surface a clean, accurate error.
      const err = new Error(PAUSED_DETAIL);
      err.response = { status: 503, data: { detail: PAUSED_DETAIL } };
      err.isPausedPreview = true;
      return Promise.reject(err);
    }
    return response;
  },
  async (error) => {
    // Errors carrying a paused-preview HTML body also get normalized.
    if (error?.response && _looksLikeWakePage(error.response)) {
      if (!error.config.__pauseRetried) {
        error.config.__pauseRetried = true;
        await _wait(1200);
        try {
          return await api.request(error.config);
        } catch (_e) {
          /* fall through to normalized error below */
        }
      }
      const normalized = new Error(PAUSED_DETAIL);
      normalized.response = { status: 503, data: { detail: PAUSED_DETAIL } };
      normalized.isPausedPreview = true;
      return Promise.reject(normalized);
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
